export interface SearchInConfig {
    /**
     * Attributes to exclude from the "Search In" dropdown (blacklist approach).
     * These attributes will never appear in the dropdown, even if they exist in the schema.
     */
    blacklistedAttributes: string[];
    /**
     * Attributes to exclude per entity type (case-insensitive key)
     */
    perEntityBlacklistedAttributes: Record<string, string[]>;
}

/**
 * Blacklist configuration for "Search In" dropdown options.
 * All schema attributes are shown by default, except those listed here.
 */
export const SEARCH_IN_CONFIG: SearchInConfig = {
    blacklistedAttributes: [],
    perEntityBlacklistedAttributes: {}
};

/**
 * Pinned attributes that appear at the top of the dropdown when the entity has them (in this order).
 * Only attributes that exist on the current entity's schema are included.
 */
const PINNED_ATTRIBUTES_ORDER = ['DisplayName', 'Id', 'UserPrincipalName'];

function dedupeCaseInsensitive(values: string[]): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const v of values) {
        const key = (v || '').toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        result.push(v);
    }
    return result;
}

/**
 * Get the ordered list of attributes for the "Search In" dropdown using blacklist approach.
 * - Always includes pinned attributes at the top: DisplayName, Id, UserPrincipalName (in that order)
 * - Then includes all other schema attributes (sorted A→Z, case-insensitive), excluding blacklisted ones
 */
export function getSearchInAttributes(entityType?: string): string[] {
    const key = (entityType || '').toLowerCase();
    
    const blacklisted = [
        ...(SEARCH_IN_CONFIG.blacklistedAttributes || []),
        ...((key && SEARCH_IN_CONFIG.perEntityBlacklistedAttributes[key]) || [])
    ];
    const blacklistedLower = new Set(blacklisted.map(b => b.toLowerCase()));
    
    // Pinned attributes (when no schema: return default order)
    return PINNED_ATTRIBUTES_ORDER;
}

/**
 * Get search attributes for a schema using blacklist approach.
 * - Pinned attributes (DisplayName, Id, UserPrincipalName) always appear first
 * - All other schema attributes appear after, excluding blacklisted ones
 * - Returns an object with pinned and other attributes separated for UI rendering
 */
export interface SearchInAttributesResult {
    pinned: string[];
    other: string[];
    all: string[];
}

export function getSearchInAttributesForSchema(
    entityType: string | undefined,
    availableAttributeNames: Iterable<string> | null | undefined
): SearchInAttributesResult {
    const key = (entityType || '').toLowerCase();
    
    const blacklisted = [
        ...(SEARCH_IN_CONFIG.blacklistedAttributes || []),
        ...((key && SEARCH_IN_CONFIG.perEntityBlacklistedAttributes[key]) || [])
    ];
    const blacklistedLower = new Set(blacklisted.map(b => b.toLowerCase()));
    
    const availableSet = availableAttributeNames
        ? new Set([...availableAttributeNames].map((a) => String(a).toLowerCase()))
        : null;
    // Pinned attributes only if they exist on this entity's schema (so e.g. UserPrincipalName only for Person types)
    const pinned = availableSet
        ? PINNED_ATTRIBUTES_ORDER.filter((p) => availableSet.has(p.toLowerCase()))
        : [...PINNED_ATTRIBUTES_ORDER];
    
    const pinnedLower = new Set(pinned.map(p => p.toLowerCase()));
    const other: string[] = [];
    
    if (availableAttributeNames) {
        for (const attr of availableAttributeNames) {
            const attrLower = String(attr).toLowerCase();
            if (pinnedLower.has(attrLower) || blacklistedLower.has(attrLower)) {
                continue;
            }
            other.push(String(attr));
        }
    }
    
    const normalizedOther = dedupeCaseInsensitive(other)
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    
    const allList = [...pinned, ...normalizedOther];
    const defaultMin = ['DisplayName', 'Id'];
    const all = allList.length > 0 ? allList : defaultMin;
    const pinnedFinal = pinned.length > 0 ? pinned : defaultMin;
    return {
        pinned: pinnedFinal,
        other: normalizedOther,
        all
    };
}

